import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, Heart } from 'lucide-react';
import { api } from '../services/api';

export default function ConfirmerEmailPage() {
  const [searchParams] = useSearchParams();
  const { token: urlToken } = useParams(); // Token from URL path for Heartbeats
  const [status, setStatus] = useState('loading'); // loading, success, already, error
  const [prenom, setPrenom] = useState('');
  const [codePromo, setCodePromo] = useState('');
  const [isHeartbeats, setIsHeartbeats] = useState(false);
  const token = searchParams.get('token') || urlToken; // Support both query param and URL path

  useEffect(() => {
    const confirmEmail = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      // Déterminer si c'est un token Heartbeats (urlToken) ou LPE (searchParams)
      const isHbToken = !!urlToken;
      setIsHeartbeats(isHbToken);

      try {
        if (isHbToken) {
          // Confirmation Heartbeats
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeats-autoresponder/confirm/${token}`);
          const result = await response.json();
          
          if (!response.ok) {
            throw new Error(result.detail || 'Erreur de confirmation');
          }
          
          setPrenom(result.first_name || '');
          if (result.already_confirmed) {
            setStatus('already');
          } else {
            setStatus('success');
          }
        } else {
          // Confirmation LPE (ancien système)
          const result = await api.confirmProspectEmail(token);
          setPrenom(result.prenom || '');
          setCodePromo(result.codePromo || 'BIENVENUE10');
          if (result.alreadyConfirmed) {
            setStatus('already');
          } else {
            setStatus('success');
          }
        }
      } catch (error) {
        console.error('Erreur confirmation:', error);
        setStatus('error');
      }
    };

    confirmEmail();
  }, [token, urlToken]);

  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1a0030 50%, #0a0015 100%)' }}>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center max-w-md w-full"
      >
        {status === 'loading' && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block mb-6"
            >
              <Loader className="w-16 h-16 text-yellow-400" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white">Vérification en cours...</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="inline-block mb-6"
            >
              {isHeartbeats ? (
                <Heart className="w-20 h-20 text-pink-400 fill-pink-400" />
              ) : (
                <CheckCircle className="w-20 h-20 text-green-400" />
              )}
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-4">
              {isHeartbeats ? '💜 Bienvenue sur Heartbeats !' : 'Email confirmé ! 🎉'}
            </h1>
            <p className="text-gray-300 mb-6">
              {isHeartbeats 
                ? `Merci ${prenom} ! Ton inscription est confirmée. Check tes emails pour la suite 💜`
                : `Merci ${prenom}, votre email est maintenant vérifié.`
              }
            </p>
            
            {isHeartbeats ? (
              // Contenu Heartbeats
              <div 
                className="p-6 rounded-2xl mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                  border: '2px solid #a855f7',
                  boxShadow: '0 0 30px rgba(168, 85, 247, 0.3)'
                }}
              >
                <p className="text-gray-300 mb-4">Prochaine étape :</p>
                <motion.div 
                  animate={{ 
                    textShadow: [
                      '0 0 10px rgba(168, 85, 247, 0.5)',
                      '0 0 30px rgba(168, 85, 247, 1)',
                      '0 0 10px rgba(168, 85, 247, 0.5)'
                    ]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-2xl font-bold text-purple-400"
                >
                  Crée ton profil Heartbeats !
                </motion.div>
                <p className="text-purple-300 text-sm mt-2">Des célibataires t'attendent 💕</p>
              </div>
            ) : (
              // Contenu LPE (code promo)
              <div 
                className="p-6 rounded-2xl mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)',
                  border: '2px solid #00FF00',
                  boxShadow: '0 0 30px rgba(0, 255, 0, 0.3)'
                }}
              >
                <p className="text-gray-300 mb-4">Votre code promo exclusif :</p>
                <motion.div 
                  animate={{ 
                    textShadow: [
                      '0 0 10px rgba(0, 255, 0, 0.5)',
                      '0 0 30px rgba(0, 255, 0, 1)',
                      '0 0 10px rgba(0, 255, 0, 0.5)'
                    ]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-4xl font-bold text-green-400 tracking-wider"
                >
                  {codePromo}
                </motion.div>
                <p className="text-green-400 text-sm mt-2">-10% sur votre 1ère visite !</p>
              </div>
            )}

            <p className="text-gray-400 text-sm mb-6">
              {isHeartbeats 
                ? '📧 Tu vas recevoir des emails pour t\'aider à trouver l\'amour !'
                : '📧 Un email avec votre code promo vous a été envoyé !'
              }
            </p>

            <div className="space-y-3">
              {isHeartbeats ? (
                <Link 
                  to="/heartbeats"
                  className="block w-full py-3 px-6 rounded-xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)' }}
                >
                  💜 Créer mon profil Heartbeats
                </Link>
              ) : (
                <>
                  <a 
                    href="/client"
                    className="block w-full py-3 px-6 rounded-xl font-bold text-black"
                    style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00FFFF 100%)' }}
                  >
                    Accéder à mon espace client
                  </a>
                  <a 
                    href="/livraison"
                    className="block w-full py-3 px-6 rounded-xl font-bold text-white border-2 border-cyan-500"
                  >
                    🚚 Me faire livrer
                  </a>
                </>
              )}
            </div>
          </>
        )}

        {status === 'already' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-block mb-6"
            >
              <CheckCircle className="w-20 h-20 text-yellow-400" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-4">Déjà confirmé !</h1>
            <p className="text-gray-300 mb-6">Votre email {prenom ? `(${prenom})` : ''} est déjà vérifié.</p>
            
            <div 
              className="p-6 rounded-2xl mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)',
                border: '2px solid #00FF00'
              }}
            >
              <p className="text-gray-300 mb-2">Votre code promo :</p>
              <div className="text-3xl font-bold text-green-400">{codePromo}</div>
            </div>

            <a 
              href="/client"
              className="block w-full py-3 px-6 rounded-xl font-bold text-black"
              style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00FFFF 100%)' }}
            >
              Accéder à l'espace client
            </a>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-block mb-6"
            >
              <XCircle className="w-20 h-20 text-red-400" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-4">Lien invalide</h1>
            <p className="text-gray-300 mb-6">Ce lien de confirmation est invalide ou a expiré.</p>
            
            <a 
              href="/offre-bienvenue"
              className="block w-full py-3 px-6 rounded-xl font-bold text-black"
              style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)' }}
            >
              Réessayer l'inscription
            </a>
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            La Petite Épicerie<br/>
            <span className="text-cyan-400">la-petite-epicerie.shop</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
